import { Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import * as React from "react";
import styled, { css } from "styled-components";

export const ContainerBaseStyle = css`
  width: 100%;
  margin: 0 auto;
  padding: 0 1rem;

  @media screen and (min-width: 576px) {
    max-width: 540px;
  }
  @media screen and (min-width: 768px) {
    max-width: 720px;
  }
  @media screen and (min-width: 1024px) {
    max-width: 1000px;
  }
  @media screen and (min-width: 1200px) {
    max-width: 1140px;
  }
`;

export const H1Base = css`
  font-family: ${({ theme }) => theme.font.title};
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: 700;
  transition: all 200ms linear;

  @media screen and (min-width: 768px) {
    font-size: ${({ theme }) => theme.fontSize.xl4};
  }
`;

export const H2Base = css`
  font-family: ${({ theme }) => theme.font.main};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 400;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all 200ms linear;

  @media screen and (min-width: 576px) {
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`;

export const Container = styled.div`
  ${ContainerBaseStyle};
`;

export const TitleContentBase = styled.div`
  width: 100%;
  text-align: center;
  padding: 0 1.25rem;

  @media screen and (min-width: 576px) {
    padding: 0;
  }

  h2 {
    ${H2Base};
    margin-bottom: 0.75rem;
  }

  h1 {
    letter-spacing: 1px;
    margin-bottom: 0;

    @media screen and (min-width: 576px) {
      font-size: ${({ theme }) => theme.fontSize.xl2};
    }

    ${H1Base};
  }

  hr {
    max-width: 80px;
    margin: 1.75rem auto 2.25rem;
    transition: all 200ms linear;

    @media screen and (min-width: 576px) {
      max-width: 100px;
      margin: 3rem auto;
    }
  }
`;

const toScale = (size) => {
  if (!size) return undefined;
  const trimmed = String(size).trim();
  if (trimmed.endsWith("%")) return parseFloat(trimmed) / 100;
  const num = parseFloat(trimmed);
  return Number.isNaN(num) ? undefined : num;
};

const BGImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
`;

/* gatsby-plugin-image reserves its own aspect-ratio box by default; these
   overrides make the image fill BGImageWrapper instead, the documented
   pattern for using GatsbyImage as a background layer. */
const BGImageLayer = styled(GatsbyImage)`
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  z-index: -1;

  img {
    filter: brightness(${(props) => props.$brightness ?? "0.4"});
    /* gatsby-plugin-image ships a global rule, [data-main-image]{transform:
       translateZ(0)}, with higher specificity than a plain "img" selector —
       !important is needed here so the zoom isn't silently dropped on the
       loaded image (it would otherwise only show on the blur-up placeholder). */
    ${(props) =>
      props.$zoom &&
      css`
        transform: scale(${props.$zoom}) !important;
      `}
  }
`;

const BGImageContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const BGImageBase = React.forwardRef(
  (
    { className, image, alt, critical, brightness, size, position, children, ...rest },
    ref
  ) => (
    <BGImageWrapper ref={ref} className={className} {...rest}>
      <BGImageLayer
        image={image}
        alt={alt || ""}
        objectFit="cover"
        objectPosition={position ?? "center"}
        loading={critical ? "eager" : "lazy"}
        $brightness={brightness}
        $zoom={toScale(size)}
      />
      <BGImageContent>{children}</BGImageContent>
    </BGImageWrapper>
  )
);

BGImageBase.displayName = "BGImageBase";

export const ReturnToHomeButton = styled(Link)`
  width: 200px;
  padding: 0.5rem 1rem;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text};
  background-color: transparent;
  cursor: pointer;
  text-transform: uppercase;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.text};
  outline: none;
  transition: all 0.25s ease;

  @media screen and (min-width: 576px) {
    width: 250px;
    font-size: ${({ theme }) => theme.fontSize.sm};
    padding: 0.75rem 2rem;
  }

  :hover {
    background-color: ${({ theme }) => theme.colors.grayLight};
  }
`;
